// =============================================================================
// readFreshness — "is the copy this tablet already holds fresh enough to skip
// asking the backend again?"
//
// PLAIN ENGLISH. The Apps Script backend is slow when it is busy — a checklist
// day read measured 12 s on its own and 83 s while the reviews banner's three
// reads were running (Trafford, 20 Sep 2026) — and every read a tablet fires
// makes every OTHER read slower, because they all queue on the same script. So
// a tablet must not re-fetch what it fetched a moment ago just because a screen
// re-mounted or the next staffer logged in. The cached copy on the tablet already
// carries a `ts` (stamped by the same code that fetched it); this decides
// whether that copy is fresh.
//
// Pure. Fails SAFE towards fetching: no ts, a bad ts, a future ts, a copy from a
// different day than asked for — all count as NOT fresh, so a broken cache can
// only ever cost one extra fetch, never a stale screen.
// =============================================================================

// `cached` is the parsed cache object ({ts, date?, ...}). `today` (optional) is
// the London day the caller wants — a copy labelled with any other day is stale
// whatever its ts says.
export function readIsFresh(cached, { maxAgeMs, now = Date.now(), today = null } = {}) {
  if (!cached || typeof cached !== "object") return false;
  if (!(maxAgeMs > 0)) return false;
  if (today != null && cached.date !== today) return false;
  const ts = Number(cached.ts);
  if (!Number.isFinite(ts) || ts <= 0) return false;
  const age = now - ts;
  if (age < 0) return false;            // clock went backwards / future stamp: don't trust it
  return age < maxAgeMs;
}

// The same decision straight from a raw localStorage string — the shape every
// caller has. A missing or unparseable value is simply "not fresh".
export function storedReadIsFresh(raw, opts) {
  try {
    if (!raw) return false;
    return readIsFresh(JSON.parse(raw), opts);
  } catch (e) { return false; }
}
